import { useCallback, useRef, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { ladeAlleSeiten } from '../services/seitenweise';
import {
    Coordinates,
    FindNearbyOptions,
    NEARBY_PROJECT_SELECT,
    NearbyProject,
    NearbyProjectRow,
    findNearbyProjects,
    isStaleGeneration,
    planGeocodeFailure,
    planNearbyLookup,
} from '../services/nearbyProjects';

// Unreiner Teil des Umfeld-Checks: Adresse -> Koordinaten (Nominatim) und
// Projektabruf aus Supabase. Die Ableitung selbst liegt rein in
// src/services/nearbyProjects.ts. Ein gemeinsamer Hook, damit die Projektanlage
// (ProjectSetupWizard) und die Auftragsannahme (AcceptQuoteDialog) exakt
// dieselbe Quelle und denselben Endpunkt nutzen.

/**
 * Geocoding ueber Nominatim — identischer Endpunkt und User-Agent wie bisher in
 * ProjectSetupWizard.handleGeocode und crm/verifyAddress.ts. Fehler und
 * Nicht-Treffer liefern null; der Aufrufer zeigt dann "Entfernung unbekannt".
 */
export const geocodeAddress = async (query: string): Promise<Coordinates | null> => {
    const q = (query || '').trim();
    if (!q) return null;
    try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`;
        const res = await fetch(url, { headers: { 'User-Agent': 'SiGeKo-App' } });
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
            const lat = parseFloat(data[0].lat);
            const lng = parseFloat(data[0].lon);
            if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
        }
        return null;
    } catch {
        return null;
    }
};

export interface UseNearbyProjectsResult {
    coordinates: Coordinates | null;
    geocoding: boolean;
    checking: boolean;
    projects: NearbyProject[];
    /** true, sobald eine Adresse gesucht, aber nicht verortet werden konnte. */
    geocodeFailed: boolean;
    /** Adresse verorten und Umfeld pruefen. Liefert die Koordinaten oder null. */
    lookup: (address: string, options?: FindNearbyOptions) => Promise<Coordinates | null>;
    reset: () => void;
}

export const useNearbyProjects = (): UseNearbyProjectsResult => {
    const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
    const [geocoding, setGeocoding] = useState(false);
    const [checking, setChecking] = useState(false);
    const [projects, setProjects] = useState<NearbyProject[]>([]);
    const [geocodeFailed, setGeocodeFailed] = useState(false);

    // Letzte ERFOLGREICH verortete Adresse und deren Koordinaten. Die
    // Adressfelder loesen onBlur auch ohne Aenderung ein Lookup aus; ohne diese
    // Merker wuerde ein transienter Nominatim-Fehler (Timeout, Rate-Limit)
    // gueltige Koordinaten wegwerfen und das Projekt ohne Standort entstehen.
    const lastSuccessRef = useRef<string>('');
    const coordsRef = useRef<Coordinates | null>(null);
    // Laufende Nummer je Lookup. Die Adressfelder starten ueberlappende Abfragen
    // (Strasse -> PLZ -> Ort); eine spaet eintreffende Antwort zur aelteren,
    // unvollstaendigeren Adresse darf das Ergebnis der neueren nicht ueberschreiben.
    const generationRef = useRef(0);

    const reset = useCallback(() => {
        generationRef.current += 1; // laufende Antworten verwerfen
        lastSuccessRef.current = '';
        coordsRef.current = null;
        setCoordinates(null);
        setProjects([]);
        setGeocodeFailed(false);
    }, []);

    const lookup = useCallback(async (address: string, options?: FindNearbyOptions) => {
        const query = (address || '').trim();
        const plan = planNearbyLookup(query, lastSuccessRef.current);
        if (plan === 'clear') {
            reset();
            return null;
        }
        // Unveraenderte Adresse: kein zweiter Nominatim-Aufruf, Ergebnis steht schon.
        // Kein Guard noetig — vor diesem Rueckgabepunkt liegt kein await.
        if (plan === 'skip') return coordsRef.current;

        const generation = (generationRef.current += 1);
        setGeocoding(true);
        setGeocodeFailed(false);
        let coords: Coordinates | null = null;
        try {
            coords = await geocodeAddress(query);
        } finally {
            // Ueberholte Laeufe lassen den Spinner stehen: er gehoert der neueren Abfrage.
            if (!isStaleGeneration(generation, generationRef.current)) setGeocoding(false);
        }
        // Ueberholt: Ergebnis komplett verwerfen, weder Erfolgs- noch Fehlschlagpfad.
        if (isStaleGeneration(generation, generationRef.current)) return coordsRef.current;
        if (!coords) {
            // Nicht fatal: die Auswahl bleibt moeglich, nur ohne Entfernungen.
            setGeocodeFailed(true);
            if (planGeocodeFailure(query, lastSuccessRef.current) === 'keep') {
                // Transienter Fehler bei gleicher Adresse: Stand unangetastet lassen.
                return coordsRef.current;
            }
            lastSuccessRef.current = '';
            coordsRef.current = null;
            setCoordinates(null);
            setProjects([]);
            return null;
        }
        lastSuccessRef.current = query;
        coordsRef.current = coords;
        setCoordinates(coords);
        setChecking(true);
        try {
            // Schlank und seitenweise (23.10.2026): frueher kam je Check der
            // ganze `data`-Blob aller Projekte (PROD 16,1 MB), und ohne
            // `.range()` haette PostgREST ab 1000 Projekten still gekappt.
            // `order('id')` haelt die Seiten eindeutig. Ein Abfragefehler wird
            // geworfen und landet unten im catch: Warnung + leere Liste.
            const zeilen = await ladeAlleSeiten<NearbyProjectRow>((von, bis) =>
                supabase.from('projects').select(NEARBY_PROJECT_SELECT).order('id').range(von, bis));
            // Auch hier: nur die juengste Abfrage schreibt die Nachbarliste.
            if (!isStaleGeneration(generation, generationRef.current)) {
                setProjects(findNearbyProjects(zeilen, coords, options));
            }
        } catch (e) {
            console.warn('Umfeld-Check fehlgeschlagen:', e);
            if (!isStaleGeneration(generation, generationRef.current)) setProjects([]);
        } finally {
            if (!isStaleGeneration(generation, generationRef.current)) setChecking(false);
        }
        return coords;
    }, [reset]);

    return { coordinates, geocoding, checking, projects, geocodeFailed, lookup, reset };
};
